import { store } from 'redux/store';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { TradingOfferOperation } from 'types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetTradingOfferOperationStatusOptions } from 'modules/trading/hooks/useGetTradingOfferOperationStatusOptions';
import { updateTradingOfferOperation } from 'modules/trading/redux/tradingOfferOperationActions';
import { TradingOfferOperationStatus } from 'types/TradingOfferOperation';

import { EditTradingOfferOperationStatusModalContextType } from '../types/EditTradingOfferOperationModalContextType';
import { editTradingOfferOperationStatusModalInitValue } from '../constants/editTradingOfferOperationStatusModalInitValue';

export const EditTradingOfferOperationStatusModalContext =
  createContext<EditTradingOfferOperationStatusModalContextType>(
    editTradingOfferOperationStatusModalInitValue
  );

export const EditTradingOfferOperationStatusModalProvider: FC<{
  children: ReactNode;
  tradingOfferOperation: TradingOfferOperation;
}> = ({ children, tradingOfferOperation }) => {
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: statusOptionsData, actions: statusOptionsActions } =
    useGetTradingOfferOperationStatusOptions({
      tradingOfferOperationStatus: tradingOfferOperation?.status,
      showAllOptions: true,
    });

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleUpdateTradingOfferOperationStatus = async () => {
    try {
      setIsLoading(true);

      await store.dispatch(
        updateTradingOfferOperation({
          tradingOfferOperationId: tradingOfferOperation?._id,
          tradingOfferOperationData: {
            status: statusOptionsData.selectedStatusOption
              ?.value as TradingOfferOperationStatus,
          },
        })
      );

      successSwal(t('OperationStatusUpdatedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorUpdatingOperationStatus'));
    } finally {
      handleCloseModal();
      setIsLoading(false);
    }
  };

  return (
    <EditTradingOfferOperationStatusModalContext.Provider
      value={{
        data: {
          tradingOfferOperation,
          selectedStatusOption: statusOptionsData.selectedStatusOption,
          statusOptions: statusOptionsData.statusOptions,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeStatusOption: statusOptionsActions.handleChangeOption,
          handleUpdateTradingOfferOperationStatus,
        },
      }}
    >
      {children}
    </EditTradingOfferOperationStatusModalContext.Provider>
  );
};

export const useEditTradingOfferOperationStatusModal = () => {
  const context = useContext(EditTradingOfferOperationStatusModalContext);
  if (!context) {
    throw new Error(
      'useEditTradingOfferOperationStatusModal must be used within a EditTradingOfferOperationStatusModalProvider'
    );
  }
  return context;
};
